import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress
} from "@mui/material";

import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

import { getProjectsOverview } from "../../services/reportService";
import uiConfig from "../../config/uiConfig";

const PALETTE = uiConfig.chartPalette;
const STATUS_COLORS = { Active: PALETTE[0], Completed: PALETTE[1], Upcoming: PALETTE[2] };

function ProjectsOverviewReport({ departmentId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    getProjectsOverview(departmentId)
      .then(setData)
      .catch((error) => console.error("Projects Overview Error:", error))
      .finally(() => setLoading(false));
  }, [departmentId]);

  if (loading) {
    return (
      <Box height={330} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  const byStatus = data?.by_status || [];
  const byType = data?.by_type || [];

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
          Projects Overview
        </Typography>

        <Chip
          size="small"
          label={`${data?.total_projects || 0} total`}
          sx={{ fontWeight: 700, background: "#eef2ff", color: "#4338ca" }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>
            By Status
          </Typography>

          <ResponsiveContainer width="100%" height={230}>
            <PieChart>
              <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {byStatus.map((entry) => (
                  <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || PALETTE[3]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", mb: 1 }}>
            By Type
          </Typography>

          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={byType}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {byType.map((entry, index) => (
                  <Cell key={entry.name} fill={PALETTE[index % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Paper>
  );
}

export default ProjectsOverviewReport;
